import { useState } from 'react';
import { useStore } from '@/store';
import { buildShareUrl, encodeShareHash, exportCharacterJson } from '@/lib/serialize';
import { CornerBrackets } from '@/components/ui/CornerBrackets';
import { InfoHint } from '@/components/ui/InfoHint';

export function ShareBar() {
  const c = useStore((s) => s.character!);
  const activeId = useStore((s) => s.activeId);
  const removeCharacter = useStore((s) => s.removeCharacter);
  const [copyState, setCopyState] = useState<'idle' | 'ok' | 'toolong'>('idle');

  async function copyShare() {
    const url = buildShareUrl(c);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Copy this link', url);
    }
    const hash = encodeShareHash(c);
    setCopyState(hash.length > 7000 ? 'toolong' : 'ok');
    setTimeout(() => setCopyState('idle'), 3500);
  }

  function download() {
    const blob = new Blob([exportCharacterJson(c)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(c.name || 'character').replace(/[^a-z0-9]+/gi, '-')}.arsmagica.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="card space-y-3">
      <CornerBrackets />
      <div className="font-display text-[11px] uppercase tracking-[0.28em] text-gold-400 flex items-center">
        Share &amp; Save
        <InfoHint side="left">
          Your whole character is packed into the URL hash (after <code>#c=</code>)
          — no account or server needed. Paste the link anywhere. Characters also
          autosave to this browser's local storage.
          <br />
          <strong>Export JSON</strong> downloads a <code>.arsmagica.json</code> file
          for archival or to re-import later.
        </InfoHint>
      </div>
      <button className="btn-primary w-full" onClick={copyShare}>
        Copy share link
      </button>
      {copyState === 'ok' && <div className="text-xs text-emerald-300">Link copied.</div>}
      {copyState === 'toolong' && (
        <div className="text-xs text-amber-300">
          Link copied, but the character is large. Prefer JSON export for archival.
        </div>
      )}
      <button className="btn-secondary w-full" onClick={download}>
        Export JSON
      </button>
      <button
        className="btn-quiet w-full"
        onClick={() => {
          if (!activeId) return;
          if (confirm('Remove this character from your library?')) {
            removeCharacter(activeId);
            window.location.hash = '#/';
          }
        }}
      >
        Remove from library
      </button>
    </div>
  );
}
